import React, { useEffect } from 'react';
import { X, CheckCircle2 } from 'lucide-react';

// ---- Merge variable substitution ----

const EXAMPLE_VALUES: Record<string, string> = {
  'member.firstName': 'Sarah',
  'member.lastName': 'Chen',
  'member.fullName': 'Sarah Chen',
  'cu.name': 'Cascade Credit Union',
  'cu.phone': '(800) 555-0100',
  'loan.type': 'Auto Loan',
  'loan.amount': '$18,750.00',
  'loan.term': '60 months',
  'account.type': 'Savings',
  'account.number_last4': '4821',
};

function resolveExampleValues(html: string): string {
  return html.replace(
    /\{\{([^}]+)\}\}/g,
    (_, key: string) => EXAMPLE_VALUES[key.trim()] ?? `[${key.trim()}]`,
  );
}

// ---- Props ----

interface CampaignEmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignName: string;
  emailBody: string; // HTML string (already rendered or plain HTML from campaign)
  onApprove?: () => void;        // called when user clicks Approve
  campaignStatus?: string;       // current campaign status — hides Approve when already 'approved'
}

/**
 * CampaignEmailPreviewModal — renders a campaign email body inside an iframe.
 *
 * Merge variables ({{variable}}) are substituted with example values before
 * rendering. Uses iframe srcdoc for CSS isolation — no dangerouslySetInnerHTML.
 *
 * Close on Escape key support included.
 *
 * Approval workflow: shows Approve Campaign button when onApprove is provided
 * and campaignStatus !== 'approved'. Shows Approved badge when already approved.
 */
export function CampaignEmailPreviewModal({
  isOpen,
  onClose,
  campaignName,
  emailBody,
  onApprove,
  campaignStatus,
}: CampaignEmailPreviewModalProps): React.ReactElement | null {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const resolvedHtml = resolveExampleValues(emailBody);
  const wrappedHtml = `<!DOCTYPE html><html><head><style>body{font-family:system-ui,-apple-system,sans-serif;padding:24px;color:#1f2937;line-height:1.6;}</style></head><body>${resolvedHtml}</body></html>`;

  return (
    /* Fixed overlay — z-50 ensures it renders above sticky header (z-30) */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label={`Email preview for ${campaignName}`}
    >
      {/* Modal container */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-sunken">
          <div>
            <p className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-0.5">
              Email Preview
            </p>
            <h2 className="text-base font-heading font-semibold text-ink leading-tight">
              {campaignName}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink-soft transition-colors mt-0.5"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Email iframe */}
        <div className="flex-1 overflow-hidden px-5 py-4">
          <iframe
            srcDoc={wrappedHtml}
            title="Email preview"
            className="w-full border-0 rounded-lg flex-1"
            style={{ minHeight: '400px' }}
            sandbox="allow-same-origin"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 pb-5 pt-3 border-t border-sunken">
          {/* Left side: approve button or approved badge */}
          <div>
            {onApprove && campaignStatus !== 'approved' && (
              <button
                type="button"
                onClick={onApprove}
                className="bg-pass hover:bg-pass text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Approve Campaign
              </button>
            )}
            {campaignStatus === 'approved' && (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-pass">
                <CheckCircle2 className="h-4 w-4" /> Approved
              </span>
            )}
          </div>

          {/* Right side: close button */}
          <button
            type="button"
            onClick={onClose}
            className="bg-sunken hover:bg-line text-ink-soft px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
